/**
 * /saas/payroll-sync — External payroll provider sync (Gusto + Rippling).
 *
 * Lets accountants connect Gusto or Rippling via OAuth, trigger a sync for
 * a date range, review the fetched pay runs, configure GL mappings, and
 * post individual runs to the GL — all without manual CSV exports.
 *
 * API surface:
 *   GET  /api/saas/payroll/connections         — list active connections
 *   DELETE /api/saas/payroll/connections/{id}  — revoke a connection
 *   GET  /api/saas/payroll/connect/gusto       — OAuth redirect (Gusto)
 *   GET  /api/saas/payroll/connect/rippling    — OAuth redirect (Rippling)
 *   POST /api/saas/payroll/sync                — fetch runs for date range
 *   GET  /api/saas/payroll/runs                — list imported pay runs
 *   POST /api/saas/payroll/runs/{id}/post      — post run to GL
 *   POST /api/saas/payroll/runs/{id}/skip      — skip a run
 *   GET  /api/saas/payroll/gl-mappings         — list GL account mappings
 *   PUT  /api/saas/payroll/gl-mappings         — bulk-update GL mappings
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import SyncIcon from "@mui/icons-material/Sync";
import PostAddIcon from "@mui/icons-material/PostAdd";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { api, errorMessage } from "@/lib/api";

export const Route = createFileRoute("/_auth/saas/payroll-sync")({
  component: PayrollSyncPage,
});

// ── Types ─────────────────────────────────────────────────────────────────────

interface Connection {
  id: string;
  provider: "gusto" | "rippling";
  provider_company_id: string;
  status: "active" | "error" | "revoked";
  scopes: string | null;
  last_sync_at: string | null;
  created_at: string;
}

interface PayrollRun {
  id: string;
  provider: string;
  provider_run_id: string;
  pay_date: string;
  period_start: string;
  period_end: string;
  employee_count: number;
  gross_wages: string;
  net_pay: string;
  currency: string;
  status: "pending" | "reviewing" | "posted" | "skipped" | "error";
  journal_id: string | null;
  review_note: string | null;
  posted_at: string | null;
}

interface GlMapping {
  id: string;
  category: string;
  account_id: string;
}

// ── Category labels ───────────────────────────────────────────────────────────

const CATEGORY_LABELS: Record<string, string> = {
  gross_wages: "Gross Wages",
  employer_taxes: "Employer Payroll Taxes",
  benefits_employer: "Employer Benefit Contributions",
  "401k_employer": "Employer 401(k) Match",
  emp_tax_withhold: "Employee Tax Withholdings",
  benefits_employee: "Employee Benefit Deductions",
  "401k_employee": "Employee 401(k) Contributions",
  net_pay: "Net Pay / Payroll Clearing",
};

// ── Provider cards ────────────────────────────────────────────────────────────

function ProviderCard({
  provider,
  label,
  description,
  connection,
  onDisconnect,
}: {
  provider: "gusto" | "rippling";
  label: string;
  description: string;
  connection: Connection | undefined;
  onDisconnect: (id: string) => void;
}) {
  const isConnected = connection?.status === "active";
  const isError = connection?.status === "error";

  return (
    <Card variant="outlined" sx={{ flex: 1, minWidth: 260 }}>
      <CardHeader
        title={label}
        subheader={description}
        action={
          isConnected ? (
            <Chip
              icon={<CheckCircleOutlineIcon />}
              label="Connected"
              color="success"
              size="small"
              variant="outlined"
            />
          ) : isError ? (
            <Chip label="Error" color="error" size="small" variant="outlined" />
          ) : (
            <Chip label="Not connected" size="small" variant="outlined" />
          )
        }
      />
      <CardContent>
        {isConnected && connection && (
          <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
            Company ID: {connection.provider_company_id}
            {connection.last_sync_at && (
              <> · Last sync: {new Date(connection.last_sync_at).toLocaleDateString()}</>
            )}
          </Typography>
        )}
        {isError && (
          <Alert severity="error" sx={{ mb: 1.5 }} variant="outlined">
            Connection error — reconnect to refresh the token.
          </Alert>
        )}
        {isConnected || isError ? (
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<LinkOffIcon />}
            onClick={() => onDisconnect(connection!.id)}
          >
            Disconnect
          </Button>
        ) : (
          <Button
            size="small"
            variant="contained"
            href={`/api/saas/payroll/connect/${provider}`}
          >
            Connect {label}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

// ── Status chip ───────────────────────────────────────────────────────────────

function RunStatusChip({ status }: { status: PayrollRun["status"] }) {
  const map: Record<
    PayrollRun["status"],
    { label: string; color: "default" | "warning" | "success" | "error" | "info" }
  > = {
    pending: { label: "Pending review", color: "warning" },
    reviewing: { label: "Reviewing", color: "info" },
    posted: { label: "Posted", color: "success" },
    skipped: { label: "Skipped", color: "default" },
    error: { label: "Error", color: "error" },
  };
  const { label, color } = map[status] ?? { label: status, color: "default" };
  return <Chip label={label} color={color} size="small" />;
}

// ── Main page ─────────────────────────────────────────────────────────────────

function PayrollSyncPage() {
  const qc = useQueryClient();

  const [snack, setSnack] = useState<{ msg: string; severity: "success" | "error" } | null>(null);
  const [syncFrom, setSyncFrom] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().slice(0, 10);
  });
  const [syncTo, setSyncTo] = useState(new Date().toISOString().slice(0, 10));
  const [syncConnectionId, setSyncConnectionId] = useState<string | null>(null);
  const [glOpen, setGlOpen] = useState(false);
  const [skipDialog, setSkipDialog] = useState<{ id: string; note: string } | null>(null);

  // ── Queries ─────────────────────────────────────────────────────────────────

  const { data: connections = [], isLoading: connsLoading } = useQuery<Connection[]>({
    queryKey: ["payroll-connections"],
    queryFn: () => api("/api/saas/payroll/connections"),
  });

  const { data: runs = [], isLoading: runsLoading } = useQuery<PayrollRun[]>({
    queryKey: ["payroll-runs"],
    queryFn: () => api("/api/saas/payroll/runs"),
  });

  const { data: glMappings = [] } = useQuery<GlMapping[]>({
    queryKey: ["payroll-gl-mappings"],
    queryFn: () => api("/api/saas/payroll/gl-mappings"),
    enabled: glOpen,
  });

  // ── Mutations ────────────────────────────────────────────────────────────────

  const disconnectMut = useMutation({
    mutationFn: (id: string) =>
      api(`/api/saas/payroll/connections/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payroll-connections"] });
      setSnack({ msg: "Connection revoked.", severity: "success" });
    },
    onError: (e) => setSnack({ msg: errorMessage(e), severity: "error" }),
  });

  const syncMut = useMutation({
    mutationFn: (connectionId: string) =>
      api("/api/saas/payroll/sync", {
        method: "POST",
        body: {
          connection_id: connectionId,
          date_from: syncFrom,
          date_to: syncTo,
        },
      }),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["payroll-runs"] });
      setSnack({
        msg: `Sync complete — ${data.fetched ?? 0} fetched, ${data.new_runs ?? 0} new.`,
        severity: "success",
      });
    },
    onError: (e) => setSnack({ msg: errorMessage(e), severity: "error" }),
  });

  const postMut = useMutation({
    mutationFn: (id: string) =>
      api(`/api/saas/payroll/runs/${id}/post`, { method: "POST" }),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["payroll-runs"] });
      setSnack({ msg: `Posted — journal ${data.journal_no}`, severity: "success" });
    },
    onError: (e) => setSnack({ msg: errorMessage(e), severity: "error" }),
  });

  const skipMut = useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) =>
      api(`/api/saas/payroll/runs/${id}/skip`, {
        method: "POST",
        body: { note: note || null },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payroll-runs"] });
      setSnack({ msg: "Run skipped.", severity: "success" });
      setSkipDialog(null);
    },
    onError: (e) => setSnack({ msg: errorMessage(e), severity: "error" }),
  });

  const activeConns = connections.filter((c) => c.status === "active");
  const gustoConn = connections.find((c) => c.provider === "gusto");
  const ripplingConn = connections.find((c) => c.provider === "rippling");

  const pendingRuns = runs.filter((r) => r.status === "pending" || r.status === "reviewing");
  const processedRuns = runs.filter((r) => r.status === "posted" || r.status === "skipped");

  return (
    <Box sx={{ p: 3, maxWidth: 1100 }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Payroll Sync
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Connect Gusto or Rippling to automatically pull pay runs into your GL. No manual
            CSV exports — sync once, review, and post.
          </Typography>
        </Box>
      </Stack>

      {/* Connection cards */}
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        PROVIDER CONNECTIONS
      </Typography>
      {connsLoading ? (
        <CircularProgress size={24} />
      ) : (
        <Stack direction="row" spacing={2} flexWrap="wrap" mb={3}>
          <ProviderCard
            provider="gusto"
            label="Gusto"
            description="One-time OAuth. Every pay run syncs automatically."
            connection={gustoConn}
            onDisconnect={(id) => disconnectMut.mutate(id)}
          />
          <ProviderCard
            provider="rippling"
            label="Rippling"
            description="One-time OAuth. Gross wages, taxes, benefits, 401(k) — all categories."
            connection={ripplingConn}
            onDisconnect={(id) => disconnectMut.mutate(id)}
          />
        </Stack>
      )}

      {/* Sync form — only shown when at least one connection is active */}
      {activeConns.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Sync a Date Range
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <TextField
              label="From"
              type="date"
              size="small"
              value={syncFrom}
              onChange={(e) => setSyncFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 160 }}
            />
            <TextField
              label="To"
              type="date"
              size="small"
              value={syncTo}
              onChange={(e) => setSyncTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 160 }}
            />
            {activeConns.map((conn) => (
              <Button
                key={conn.id}
                variant="contained"
                size="small"
                startIcon={syncMut.isPending && syncConnectionId === conn.id ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <SyncIcon />
                )}
                disabled={syncMut.isPending}
                onClick={() => {
                  setSyncConnectionId(conn.id);
                  syncMut.mutate(conn.id);
                }}
              >
                Sync {conn.provider === "gusto" ? "Gusto" : "Rippling"}
              </Button>
            ))}
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
            Re-syncing an already-imported pay run is safe — idempotent, won't create duplicates.
          </Typography>
        </Paper>
      )}

      {/* GL Mapping toggle */}
      <Stack direction="row" alignItems="center" mb={1}>
        <Typography variant="subtitle2" color="text.secondary">
          GL ACCOUNT MAPPINGS
        </Typography>
        <IconButton size="small" onClick={() => setGlOpen((v) => !v)} sx={{ ml: 1 }}>
          {glOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Stack>
      <Collapse in={glOpen}>
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          {glMappings.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No GL mappings configured yet. Run a sync first — default mappings are seeded
              automatically on first view.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Payroll category</TableCell>
                  <TableCell>GL account</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {glMappings.map((m) => (
                  <TableRow key={m.id}>
                    <TableCell>{CATEGORY_LABELS[m.category] ?? m.category}</TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {m.account_id}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: "block" }}>
            Default mappings follow the standard chart of accounts. Go to Settings → Integrations
            to change account assignments.
          </Typography>
        </Paper>
      </Collapse>

      {/* Pending pay runs */}
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        PENDING REVIEW
      </Typography>
      {runsLoading ? (
        <CircularProgress size={24} />
      ) : pendingRuns.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="body2" color="text.secondary">
            No pay runs awaiting review. Connect a provider and run a sync to import pay runs.
          </Typography>
        </Paper>
      ) : (
        <Paper variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Provider</TableCell>
                <TableCell>Pay date</TableCell>
                <TableCell>Period</TableCell>
                <TableCell align="right">Employees</TableCell>
                <TableCell align="right">Gross wages</TableCell>
                <TableCell align="right">Net pay</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pendingRuns.map((run) => (
                <TableRow key={run.id} hover>
                  <TableCell>
                    <Chip
                      label={run.provider}
                      size="small"
                      variant="outlined"
                      sx={{ textTransform: "capitalize" }}
                    />
                  </TableCell>
                  <TableCell>{run.pay_date}</TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {run.period_start} – {run.period_end}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{run.employee_count}</TableCell>
                  <TableCell align="right">
                    {parseFloat(run.gross_wages).toLocaleString("en-US", {
                      style: "currency",
                      currency: run.currency,
                    })}
                  </TableCell>
                  <TableCell align="right">
                    {parseFloat(run.net_pay).toLocaleString("en-US", {
                      style: "currency",
                      currency: run.currency,
                    })}
                  </TableCell>
                  <TableCell>
                    <RunStatusChip status={run.status} />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title="Post to GL">
                        <IconButton
                          size="small"
                          color="primary"
                          disabled={postMut.isPending}
                          onClick={() => postMut.mutate(run.id)}
                        >
                          <PostAddIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Skip">
                        <IconButton
                          size="small"
                          onClick={() => setSkipDialog({ id: run.id, note: "" })}
                        >
                          <SkipNextIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Posted / skipped runs */}
      {processedRuns.length > 0 && (
        <>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            POSTED / SKIPPED
          </Typography>
          <Paper variant="outlined" sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Provider</TableCell>
                  <TableCell>Pay date</TableCell>
                  <TableCell align="right">Gross wages</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Journal</TableCell>
                  <TableCell>Posted at</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {processedRuns.map((run) => (
                  <TableRow key={run.id}>
                    <TableCell sx={{ textTransform: "capitalize" }}>{run.provider}</TableCell>
                    <TableCell>{run.pay_date}</TableCell>
                    <TableCell align="right">
                      {parseFloat(run.gross_wages).toLocaleString("en-US", {
                        style: "currency",
                        currency: run.currency,
                      })}
                    </TableCell>
                    <TableCell>
                      <RunStatusChip status={run.status} />
                    </TableCell>
                    <TableCell>
                      {run.journal_id ? (
                        <Typography variant="caption" color="primary">
                          {run.journal_id.slice(0, 8)}…
                        </Typography>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>
                      {run.posted_at ? new Date(run.posted_at).toLocaleDateString() : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}

      {/* Skip dialog */}
      <Dialog open={!!skipDialog} onClose={() => setSkipDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Skip this pay run?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Skipped runs are excluded from GL posting and won't appear in the pending queue.
            You can add a note to explain why.
          </Typography>
          <TextField
            label="Note (optional)"
            fullWidth
            multiline
            rows={2}
            size="small"
            value={skipDialog?.note ?? ""}
            onChange={(e) =>
              setSkipDialog((d) => (d ? { ...d, note: e.target.value } : null))
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSkipDialog(null)}>Cancel</Button>
          <Button
            onClick={() => skipDialog && skipMut.mutate({ id: skipDialog.id, note: skipDialog.note })}
            color="warning"
            disabled={skipMut.isPending}
          >
            Skip run
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snack?.severity ?? "info"} onClose={() => setSnack(null)} variant="filled">
          {snack?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
